import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { AppShell } from '@/components/layout/AppShell';
import { AuthGate } from '@/components/AuthGate';
import { useCreateAgent } from '@/hooks/useAgent';
import { pickThreeCandidates, type AgentCandidate } from '@/lib/agent-pool';
import { cn } from '@/lib/utils';

/**
 * `/agents/new` — Spec #151 Phase 3-A.
 *
 * 御三家プール式の Agent 作成 UX。3 候補 (アバター + 名前 +
 * 一言) のみを提示し、タップで即作成 → 詳細ページに遷移する。
 * LLM provider / model は内部 default 固定。User は選ばない。
 *
 * 5 原則:
 *  - 余白: 候補は 3 つのみ、説明文ゼロ
 *  - 禅: 直感で 1 つ選ぶだけ
 *  - ミニマル: form / select 一切なし
 */
function NewAgentPage() {
  const create = useCreateAgent();
  const navigate = useNavigate();

  // mount 時に 3 候補を確定 (re-render で振り直されない)
  const candidates = useMemo(() => pickThreeCandidates(), []);

  const [picking, setPicking] = useState<string | null>(null);
  const errorMessage = create.error
    ? `作成に失敗しました (${String(create.error)})`
    : null;

  const handleTap = async (candidate: AgentCandidate) => {
    if (create.isPending) return;
    setPicking(candidate.id);
    try {
      const created = await create.mutateAsync({
        name: candidate.name,
        llmProvider: candidate.defaultProvider,
        llmModel: candidate.defaultModel,
        // Agent entity が tools.length>=1 を required にするので
        // pool の defaultTools を素直に渡す。User からは見えない。
        tools: [...candidate.defaultTools],
        // 御三家の avatar (絵文字) と tagline を extendedConfig に保存。
        // Sidebar AgentsSection は既に extendedConfig.avatar を読む実装、
        // Agent Lucid persona prompt も extendedConfig.tagline を読み込む。
        // これがないと Agent 詳細で本人らしさが失われる。
        extendedConfig: {
          avatar: candidate.avatar,
          tagline: candidate.tagline,
        },
      });
      await navigate({ to: '/agents/$agentId', params: { agentId: created.id } });
    } catch (err) {
      console.error('[gosanke] create failed', err);
      setPicking(null);
    }
  };

  return (
    <AppShell>
      <div className="mx-auto max-w-2xl px-6 py-16">
        <h1 className="text-2xl font-semibold mb-12 text-center">
          Agent を選ぶ
        </h1>
        <div
          className="grid grid-cols-1 sm:grid-cols-3 gap-6"
          role="list"
          data-testid="gosanke-grid"
        >
          {candidates.map((c) => (
            <button
              key={c.id}
              role="listitem"
              type="button"
              disabled={create.isPending}
              onClick={() => handleTap(c)}
              data-testid={`gosanke-card-${c.id}`}
              className={cn(
                'flex flex-col items-center gap-3 rounded-xl border border-border bg-card p-6 transition-colors',
                'hover:bg-accent/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent',
                'disabled:opacity-40 disabled:cursor-not-allowed',
                picking === c.id && 'bg-accent/15',
              )}
            >
              <span aria-hidden className="text-4xl leading-none">
                {c.avatar}
              </span>
              <span className="font-medium">{c.name}</span>
              <span className="text-xs text-muted-foreground">{c.tagline}</span>
            </button>
          ))}
        </div>
        {errorMessage && (
          <p
            role="alert"
            className="text-destructive text-sm text-center mt-8"
          >
            {errorMessage}
          </p>
        )}
      </div>
    </AppShell>
  );
}

export const Route = createFileRoute('/agents/new')({
  component: () => (
    <AuthGate>
      <NewAgentPage />
    </AuthGate>
  ),
});
